import { useMemo, useRef, useState, type ChangeEvent, type Dispatch, type FormEvent, type SetStateAction } from "react";

type Language = "en" | "kh";
type BudgetCurrency = "USD" | "Riel";

type Spending = {
  id: number;
  item: string;
  budget: number;
  budgetCurrency: BudgetCurrency;
  actual: number;
  actualCurrency: BudgetCurrency;
  note: string;
};

type SpendingForm = {
  item: string;
  budget: string;
  budgetCurrency: BudgetCurrency;
  actual: string;
  actualCurrency: BudgetCurrency;
  note: string;
};

type BudgetText = {
  tab: string;
  leftTitle: string;
  leftHint: string;
  totals: string;
  edit: string;
  budgetCap: string;
  currentAmountLimitWarning: string;
  inputAmountOnly: string;
  planAmount: string;
  currentAmount: string;
  leftAmount: string;
  spendingItem: string;
  spendingItemPlaceholder: string;
  planBalanceLabel: string;
  planPlaceholder: string;
  currentPlaceholder: string;
  notes: string;
  notesPlaceholder: string;
  addSpending: string;
  updateSpending: string;
  trackerTitle: string;
  lineItemsTracked: string;
  overview: string;
  table: {
    item: string;
    plan: string;
    current: string;
    left: string;
    notes: string;
    action: string;
    totalsLabel: string;
  };
  noItems: string;
  delete: string;
  totalUsd: string;
  totalRiel: string;
};

type BudgetSectionProps = {
  budgetText: Record<Language, BudgetText>;
  language: Language;
  openBudgetAmountModal: () => void;
  formatCurrency: (value: number) => string;
  displayedTotals: {
    budgetTotal: number;
    actualTotal: number;
    planUsd: number;
    planRiel: number;
    currentUsd: number;
    currentRiel: number;
    leftUsd: number;
    leftRiel: number;
  };
  handleSpendingSubmit: (event: FormEvent<HTMLFormElement>) => void;
  spendingForm: SpendingForm;
  setSpendingForm: Dispatch<SetStateAction<SpendingForm>>;
  editingSpendingId: number | null;
  spending: Spending[];
  startEditSpending: (item: Spending) => void;
  requestDeleteSpendingRow: (id: number) => void;
  onDeleteSelected: (ids: number[]) => void;
  isDeletingSelected: boolean;
  onDownloadBudgetImportSample: () => Promise<void> | void;
  onUploadBudgetFile: (
    file: File,
  ) => Promise<{ created: number; failed: number; message?: string }>;
  isUploadingBudgetFile: boolean;
};

export default function BudgetSection({
  budgetText,
  language,
  openBudgetAmountModal,
  formatCurrency,
  displayedTotals,
  handleSpendingSubmit,
  spendingForm,
  setSpendingForm,
  editingSpendingId,
  spending,
  startEditSpending,
  requestDeleteSpendingRow,
  onDeleteSelected,
  isDeletingSelected,
  onDownloadBudgetImportSample,
  onUploadBudgetFile,
  isUploadingBudgetFile,
}: BudgetSectionProps) {
  const RIELS_PER_USD = 4100;
  void displayedTotals;

  const sanitizeAmountInput = (value: string) => {
    let sanitized = "";
    let hasDecimal = false;

    for (const character of value.replace(/\s+/g, "")) {
      if (/\d/.test(character)) {
        sanitized += character;
        continue;
      }

      if (character === "," && !hasDecimal) {
        sanitized += character;
        continue;
      }

      if (character === "." && !hasDecimal) {
        sanitized += character;
        hasDecimal = true;
      }
    }

    return sanitized;
  };
  const budgetUploadInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedSpendingIds, setSelectedSpendingIds] = useState<number[]>([]);
  const [planSortDirection, setPlanSortDirection] = useState<"desc" | "asc">("desc");
  const [showBudgetImportMenu, setShowBudgetImportMenu] = useState(false);
  const [budgetImportFeedback, setBudgetImportFeedback] = useState<{
    tone: "success" | "error";
    message: string;
  } | null>(null);
  const amountPattern = /^(?:\d[\d,]*(?:\.\d*)?|\.\d*)$/;
  const invalidPlanAmount =
    spendingForm.budget.trim() !== "" && !amountPattern.test(spendingForm.budget.trim());
  const invalidCurrentAmount =
    spendingForm.actual.trim() !== "" && !amountPattern.test(spendingForm.actual.trim());
  const hasInvalidAmount = invalidPlanAmount || invalidCurrentAmount;
  const isZeroLike = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return false;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) && parsed === 0;
  };

  const formatAmountByCurrency = (value: number, currency: BudgetCurrency) => {
    if (currency === "Riel") {
      return `${new Intl.NumberFormat("km-KH").format(value)} ៛`;
    }
    return formatCurrency(value);
  };

  const budgetSummaryTotals = useMemo(() => {
    const planUsd = spending
      .filter((item) => item.budgetCurrency === "USD")
      .reduce((sum, item) => sum + item.budget, 0);
    const planRiel = spending
      .filter((item) => item.budgetCurrency === "Riel")
      .reduce((sum, item) => sum + item.budget, 0);
    const actualUsd = spending
      .filter((item) => item.actualCurrency === "USD")
      .reduce((sum, item) => sum + item.actual, 0);
    const actualRiel = spending
      .filter((item) => item.actualCurrency === "Riel")
      .reduce((sum, item) => sum + item.actual, 0);

    return {
      plan: { USD: planUsd, Riel: planRiel },
      actual: { USD: actualUsd, Riel: actualRiel },
      left: {
        USD: planUsd - actualUsd,
        Riel: planRiel - actualRiel,
      },
    };
  }, [spending]);
  const planBalanceLabel = budgetText[language].planBalanceLabel;
  const downloadLabel = language === "kh" ? "ទាញយក Excel" : "Download Excel";
  const uploadLabel = language === "kh" ? "បញ្ចូលឯកសារ" : "Upload file";
  const downloadSampleLabel = language === "kh" ? "ទាញយក Sample file" : "Download sample file";
  const uploadFileLabel = language === "kh" ? "អាប់ឡូតឯកសារ" : "Upload file";
  const uploadingLabel = language === "kh" ? "កំពុងអាប់ឡូត..." : "Uploading...";
  const selectedVisibleSpendingIds = useMemo(
    () => selectedSpendingIds.filter((id) => spending.some((item) => item.id === id)),
    [selectedSpendingIds, spending],
  );
  const allVisibleSelected = useMemo(
    () => spending.length > 0 && selectedVisibleSpendingIds.length === spending.length,
    [selectedVisibleSpendingIds.length, spending.length],
  );
  const sortedSpending = useMemo(() => {
    const toUsdValue = (item: Spending) =>
      item.budgetCurrency === "USD" ? item.budget : item.budget / RIELS_PER_USD;

    return [...spending].sort((a, b) => {
      const amountDifference = toUsdValue(a) - toUsdValue(b);
      if (amountDifference === 0) {
        return a.item.localeCompare(b.item);
      }
      return planSortDirection === "asc" ? amountDifference : -amountDifference;
    });
  }, [planSortDirection, spending]);

  const escapeHtml = (value: string | number) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const formatRowLeftAmount = (item: Spending) => {
    if (item.budgetCurrency === item.actualCurrency) {
      return formatAmountByCurrency(item.budget - item.actual, item.budgetCurrency);
    }

    const usdDelta =
      (item.budgetCurrency === "USD" ? item.budget : 0) -
      (item.actualCurrency === "USD" ? item.actual : 0);
    const rielDelta =
      (item.budgetCurrency === "Riel" ? item.budget : 0) -
      (item.actualCurrency === "Riel" ? item.actual : 0);

    return [usdDelta !== 0 ? formatAmountByCurrency(usdDelta, "USD") : null, rielDelta !== 0 ? formatAmountByCurrency(rielDelta, "Riel") : null]
      .filter(Boolean)
      .join(" | ");
  };

  const handleDownloadBudgetTracker = () => {
    const summaryRows = [
      [budgetText[language].planAmount, budgetText[language].totalUsd, formatAmountByCurrency(budgetSummaryTotals.plan.USD, "USD")],
      [budgetText[language].planAmount, budgetText[language].totalRiel, formatAmountByCurrency(budgetSummaryTotals.plan.Riel, "Riel")],
      [budgetText[language].currentAmount, budgetText[language].totalUsd, formatAmountByCurrency(budgetSummaryTotals.actual.USD, "USD")],
      [budgetText[language].currentAmount, budgetText[language].totalRiel, formatAmountByCurrency(budgetSummaryTotals.actual.Riel, "Riel")],
      [planBalanceLabel, budgetText[language].totalUsd, formatAmountByCurrency(budgetSummaryTotals.left.USD, "USD")],
      [planBalanceLabel, budgetText[language].totalRiel, formatAmountByCurrency(budgetSummaryTotals.left.Riel, "Riel")],
    ];

    const tableHeaders = [
      budgetText[language].table.item,
      budgetText[language].table.plan,
      budgetText[language].table.current,
      budgetText[language].table.left,
      budgetText[language].table.notes,
    ];

    const tableRows = spending.map((item) => [
      item.item,
      formatAmountByCurrency(item.budget, item.budgetCurrency),
      formatAmountByCurrency(item.actual, item.actualCurrency),
      formatRowLeftAmount(item),
      item.note || "-",
    ]);

    const html = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <style>
      table { border-collapse: collapse; width: 100%; }
      th, td { border: 1px solid #d4d4d8; padding: 8px 10px; text-align: left; }
      th { background: #f4f4f5; }
      .section-title { font-weight: 700; font-size: 16px; margin: 0 0 8px; }
      .spacer { height: 16px; }
    </style>
  </head>
  <body>
    <p class="section-title">${escapeHtml(budgetText[language].totals)}</p>
    <table>
      <thead>
        <tr>
          <th>${escapeHtml(budgetText[language].overview)}</th>
          <th>${escapeHtml(language === "kh" ? "រូបិយប័ណ្ណ" : "Currency")}</th>
          <th>${escapeHtml(language === "kh" ? "ចំនួន" : "Amount")}</th>
        </tr>
      </thead>
      <tbody>
        ${summaryRows
          .map(
            (row) => `<tr>${row
              .map((cell) => `<td>${escapeHtml(cell)}</td>`)
              .join("")}</tr>`,
          )
          .join("")}
      </tbody>
    </table>
    <div class="spacer"></div>
    <p class="section-title">${escapeHtml(budgetText[language].trackerTitle)}</p>
    <table>
      <thead>
        <tr>${tableHeaders.map((cell) => `<th>${escapeHtml(cell)}</th>`).join("")}</tr>
      </thead>
      <tbody>
        ${tableRows
          .map(
            (row) => `<tr>${row
              .map((cell) => `<td>${escapeHtml(cell)}</td>`)
              .join("")}</tr>`,
          )
          .join("")}
        <tr>
          <td><strong>${escapeHtml(budgetText[language].table.totalsLabel)}</strong></td>
          <td>${escapeHtml(`${formatAmountByCurrency(budgetSummaryTotals.plan.USD, "USD")} | ${formatAmountByCurrency(budgetSummaryTotals.plan.Riel, "Riel")}`)}</td>
          <td>${escapeHtml(`${formatAmountByCurrency(budgetSummaryTotals.actual.USD, "USD")} | ${formatAmountByCurrency(budgetSummaryTotals.actual.Riel, "Riel")}`)}</td>
          <td>${escapeHtml(`${formatAmountByCurrency(budgetSummaryTotals.left.USD, "USD")} | ${formatAmountByCurrency(budgetSummaryTotals.left.Riel, "Riel")}`)}</td>
          <td>-</td>
        </tr>
      </tbody>
    </table>
  </body>
</html>`;

    const blob = new Blob([html], {
      type: "application/vnd.ms-excel;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const dateStamp = new Date().toISOString().slice(0, 10);

    link.href = url;
    link.download = `budget-tracker-${dateStamp}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleBudgetFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.currentTarget.value = "";
    if (!file) {
      return;
    }

    setBudgetImportFeedback(null);
    try {
      const result = await onUploadBudgetFile(file);
      setBudgetImportFeedback({
        tone: result.failed > 0 ? "error" : "success",
        message:
          result.message ??
          (result.failed > 0
            ? `${result.created} uploaded, ${result.failed} failed.`
            : `${result.created} items uploaded successfully.`),
      });
      setShowBudgetImportMenu(false);
    } catch (error) {
      setBudgetImportFeedback({
        tone: "error",
        message: error instanceof Error ? error.message : "Unable to upload file.",
      });
    }
  };

  return (
    <div className="mt-2 grid w-full min-h-[calc(100svh-4.75rem)] gap-4 overflow-hidden pb-20 sm:mt-4 sm:h-full sm:min-h-0 sm:gap-6 sm:pb-24 lg:grid-cols-[1fr_1.5fr]">
      <div className="min-w-0 rounded-3xl border border-[color:var(--panel-border)] bg-[color:var(--card)] p-4 shadow-[var(--panel-shadow)] sm:p-7">
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-[family-name:var(--font-display)] text-2xl text-[color:var(--foreground)]">
            {budgetText[language].leftTitle}
          </h2>
        </div>
        <p className="mt-2 text-sm text-[color:var(--muted)]">{budgetText[language].leftHint}</p>
        <div className="mt-6 rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)] p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-widest text-[color:var(--muted)]">
              {budgetText[language].totals}
            </p>
            <button
              type="button"
              data-tab-trigger="true"
              onClick={openBudgetAmountModal}
              className="flex h-8 w-8 items-center justify-center rounded-full border border-amber-300 bg-amber-50 text-amber-700 shadow-sm transition hover:border-amber-400 hover:bg-amber-100 dark:border-amber-400/30 dark:bg-amber-400/15 dark:text-amber-200 dark:hover:bg-amber-400/25"
              aria-label={budgetText[language].edit}
              title={budgetText[language].edit}
            >
              <svg
                viewBox="0 0 24 24"
                className="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M3 21h6" />
                <path d="M14.5 4.5a2.12 2.12 0 0 1 3 3L8 17l-4 1 1-4 9.5-9.5Z" />
              </svg>
            </button>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm">
            <span className="font-semibold text-[color:var(--foreground)]">
              {budgetText[language].planAmount}
            </span>
            <div className="text-right">
              <div className="text-emerald-600 dark:text-emerald-400">
                {formatCurrency(displayedTotals.budgetTotal)}
              </div>
            </div>
          </div>
          <div className="mt-2 flex items-center justify-between text-sm">
            <span className="font-semibold text-[color:var(--foreground)]">
              {budgetText[language].currentAmount}
            </span>
            <div className="text-right">
              <div className="text-amber-700 dark:text-amber-400">
                {formatCurrency(displayedTotals.actualTotal)}
              </div>
            </div>
          </div>
        </div>
        <form className="mt-6 grid gap-4" onSubmit={handleSpendingSubmit}>
          <label className="grid gap-2 text-sm font-medium text-[color:var(--foreground)]">
            {budgetText[language].spendingItem}
            <input
              value={spendingForm.item}
              onChange={(event) =>
                setSpendingForm((prev) => ({
                  ...prev,
                  item: event.target.value,
                }))
              }
              className="rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 py-3 text-sm text-[color:var(--foreground)] outline-none transition focus:border-[color:var(--accent)]"
              placeholder={budgetText[language].spendingItemPlaceholder}
              required
            />
          </label>
          <label className="grid gap-2 text-sm font-medium text-[color:var(--foreground)]">
            {budgetText[language].planAmount}
            <div className="flex gap-2">
              <input
                value={spendingForm.budget}
                onChange={(event) =>
                  setSpendingForm((prev) => ({
                    ...prev,
                    budget: sanitizeAmountInput(event.target.value),
                  }))
                }
                onFocus={() => {
                  if (isZeroLike(spendingForm.budget)) {
                    setSpendingForm((prev) => ({ ...prev, budget: "" }));
                  }
                }}
                className="min-w-0 flex-1 rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 py-3 text-sm text-[color:var(--foreground)] outline-none transition focus:border-[color:var(--accent)]"
                placeholder={budgetText[language].planPlaceholder}
                inputMode="decimal"
                required
              />
              <select
                value={spendingForm.budgetCurrency}
                onChange={(event) =>
                  setSpendingForm((prev) => ({
                    ...prev,
                    budgetCurrency: event.target.value as BudgetCurrency,
                  }))
                }
                className="w-28 rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)] px-3 py-3 text-sm text-[color:var(--foreground)] outline-none transition focus:border-[color:var(--accent)]"
              >
                <option value="USD">USD</option>
                <option value="Riel">Riel</option>
              </select>
            </div>
            {invalidPlanAmount ? (
              <p className="text-xs text-red-500">{budgetText[language].inputAmountOnly}</p>
            ) : null}
          </label>
          <label className="grid gap-2 text-sm font-medium text-[color:var(--foreground)]">
            {budgetText[language].currentAmount}
            <div className="flex gap-2">
              <input
                value={spendingForm.actual}
                onChange={(event) =>
                  setSpendingForm((prev) => ({
                    ...prev,
                    actual: sanitizeAmountInput(event.target.value),
                  }))
                }
                onFocus={() => {
                  if (isZeroLike(spendingForm.actual)) {
                    setSpendingForm((prev) => ({ ...prev, actual: "" }));
                  }
                }}
                className="min-w-0 flex-1 rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 py-3 text-sm text-[color:var(--foreground)] outline-none transition focus:border-[color:var(--accent)]"
                placeholder={budgetText[language].currentPlaceholder}
                inputMode="decimal"
              />
              <select
                value={spendingForm.actualCurrency}
                onChange={(event) =>
                  setSpendingForm((prev) => ({
                    ...prev,
                    actualCurrency: event.target.value as BudgetCurrency,
                  }))
                }
                className="w-28 rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)] px-3 py-3 text-sm text-[color:var(--foreground)] outline-none transition focus:border-[color:var(--accent)]"
              >
                <option value="USD">USD</option>
                <option value="Riel">Riel</option>
              </select>
            </div>
            {invalidCurrentAmount ? (
              <p className="text-xs text-red-500">{budgetText[language].inputAmountOnly}</p>
            ) : null}
          </label>
          <label className="grid gap-2 text-sm font-medium text-[color:var(--foreground)]">
            {budgetText[language].notes}
            <input
              value={spendingForm.note}
              onChange={(event) =>
                setSpendingForm((prev) => ({
                  ...prev,
                  note: event.target.value,
                }))
              }
              className="rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 py-3 text-sm text-[color:var(--foreground)] outline-none transition focus:border-[color:var(--accent)]"
              placeholder={budgetText[language].notesPlaceholder}
            />
          </label>
          <button
            type="submit"
            data-tab-trigger="true"
            disabled={hasInvalidAmount}
            className={`rounded-full border px-5 py-3 text-sm font-bold uppercase tracking-wide transition ${
              hasInvalidAmount
                ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
                : "border-[#79572f] bg-[#8f6b3f] !text-white shadow-[0_10px_24px_rgba(111,79,43,0.28),inset_0_1px_0_rgba(255,255,255,0.18)] hover:-translate-y-0.5 hover:bg-[#79572f] active:translate-y-0 dark:border-[#b88a38] dark:bg-[#8f6b3f] dark:!text-white dark:shadow-[0_10px_28px_rgba(184,138,56,0.28)] dark:hover:bg-[#79572f]"
            }`}
          >
            {editingSpendingId != null
              ? budgetText[language].updateSpending
              : budgetText[language].addSpending}
          </button>
        </form>
      </div>
      <div className="flex min-h-0 min-w-0 flex-col rounded-3xl border border-[color:var(--panel-border)] bg-[color:var(--card)] p-4 shadow-[var(--panel-shadow)] sm:p-7">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-[family-name:var(--font-display)] text-2xl text-[color:var(--foreground)]">
              {budgetText[language].trackerTitle}
            </h3>
            <p className="mt-1 text-sm text-[color:var(--muted)]">
              {spending.length} {budgetText[language].lineItemsTracked}
            </p>
          </div>
          <div className="flex items-center justify-end gap-2">
            <input
              ref={budgetUploadInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={handleBudgetFileChange}
            />
            <span className="rounded-full bg-[color:var(--accent-soft)] px-4 py-2 text-xs font-semibold uppercase tracking-widest text-[color:var(--accent)]">
              {budgetText[language].overview}
            </span>
            <button
              type="button"
              data-tab-trigger="true"
              onClick={() => onDeleteSelected(selectedVisibleSpendingIds)}
              disabled={selectedVisibleSpendingIds.length === 0 || isDeletingSelected}
              className={`flex h-8 min-w-8 items-center justify-center rounded-full px-3 transition ${
                selectedVisibleSpendingIds.length === 0 || isDeletingSelected
                  ? "cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500"
                  : "bg-red-600 text-white shadow-sm hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600"
              }`}
              aria-label="Delete selected budget rows"
              title="Delete selected budget rows"
            >
              {isDeletingSelected ? (
                <svg viewBox="0 0 24 24" className="h-4 w-4 animate-spin" aria-hidden="true">
                  <circle
                    cx="12"
                    cy="12"
                    r="9"
                    fill="none"
                    stroke="currentColor"
                    strokeOpacity="0.3"
                    strokeWidth="2"
                  />
                  <path
                    d="M21 12a9 9 0 0 0-9-9"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              ) : (
                <>
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M3 6h18" />
                    <path d="M8 6V4.75A1.75 1.75 0 0 1 9.75 3h4.5A1.75 1.75 0 0 1 16 4.75V6" />
                    <path d="M19 6l-1 13.25A1.75 1.75 0 0 1 16.26 21H7.74A1.75 1.75 0 0 1 6 19.25L5 6" />
                    <path d="M10 10.5v6" />
                    <path d="M14 10.5v6" />
                  </svg>
                  <span className="ml-2 text-xs font-semibold">{selectedVisibleSpendingIds.length}</span>
                </>
              )}
            </button>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowBudgetImportMenu((prev) => !prev)}
                data-tab-trigger="true"
                className="flex h-8 w-8 items-center justify-center rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
                aria-label={uploadLabel}
                title={uploadLabel}
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M12 16V4" />
                  <path d="m7 9 5-5 5 5" />
                  <path d="M20 16.5v1.25A2.25 2.25 0 0 1 17.75 20h-11.5A2.25 2.25 0 0 1 4 17.75V16.5" />
                </svg>
              </button>
              {showBudgetImportMenu ? (
                <div className="absolute right-0 top-10 z-20 w-52 overflow-hidden rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)] shadow-[0_18px_45px_rgba(15,23,42,0.16)]">
                  <div className="border-b border-[color:var(--divider)] px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--muted)]">
                      {uploadLabel}
                    </p>
                  </div>
                  <div className="p-2">
                    <button
                      type="button"
                      data-tab-trigger="true"
                      onClick={async () => {
                        try {
                          await onDownloadBudgetImportSample();
                          setShowBudgetImportMenu(false);
                        } catch (error) {
                          setBudgetImportFeedback({
                            tone: "error",
                            message:
                              error instanceof Error
                                ? error.message
                                : "Unable to download sample file.",
                          });
                        }
                      }}
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        className="h-4 w-4 text-emerald-600"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M12 3v12" />
                        <path d="m7 10 5 5 5-5" />
                        <path d="M4 19h16" />
                      </svg>
                      <span>{downloadSampleLabel}</span>
                    </button>
                    <button
                      type="button"
                      data-tab-trigger="true"
                      disabled={isUploadingBudgetFile}
                      onClick={() => budgetUploadInputRef.current?.click()}
                      className={`mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition ${
                        isUploadingBudgetFile
                          ? "cursor-not-allowed text-[color:var(--muted)] opacity-70"
                          : "text-[color:var(--foreground)] hover:bg-[color:var(--surface-hover)]"
                      }`}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        className="h-4 w-4 text-amber-600"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M12 16V4" />
                        <path d="m7 9 5-5 5 5" />
                        <path d="M20 16.5v1.25A2.25 2.25 0 0 1 17.75 20h-11.5A2.25 2.25 0 0 1 4 17.75V16.5" />
                      </svg>
                      <span>{isUploadingBudgetFile ? uploadingLabel : uploadFileLabel}</span>
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={handleDownloadBudgetTracker}
              data-tab-trigger="true"
              disabled={spending.length === 0}
              className={`flex h-8 w-8 items-center justify-center rounded-full border transition ${
                spending.length === 0
                  ? "cursor-not-allowed border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--muted)] opacity-60"
                  : "border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--foreground)] hover:bg-[color:var(--surface-hover)]"
              }`}
              aria-label={downloadLabel}
              title={downloadLabel}
            >
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 3v12" />
                <path d="m7 10 5 5 5-5" />
                <path d="M4 19h16" />
              </svg>
            </button>
          </div>
        </div>
        {budgetImportFeedback ? (
          <div
            className={`mt-3 rounded-2xl border px-4 py-3 text-sm ${
              budgetImportFeedback.tone === "success"
                ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/25 dark:bg-emerald-500/12 dark:text-emerald-200"
                : "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-400/25 dark:bg-rose-500/12 dark:text-rose-200"
            }`}
          >
            {budgetImportFeedback.message}
          </div>
        ) : null}
        <div className="mt-6 min-h-0 flex-1 overflow-hidden rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)]">
          <div className="h-full min-h-[68svh] max-h-[72svh] overflow-x-auto overflow-y-auto overscroll-contain pb-4 sm:min-h-0 sm:max-h-none sm:h-full">
          <table className="min-w-[760px] w-full table-auto text-left text-sm sm:min-w-[860px] lg:min-w-0">
            <thead className="bg-[color:var(--surface-strong)] text-xs uppercase tracking-widest text-[color:var(--muted)]">
              <tr>
                <th className="sticky top-0 z-10 bg-[color:var(--surface-strong)] px-2 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={(event) => {
                      if (event.target.checked) {
                        setSelectedSpendingIds(spending.map((item) => item.id));
                        return;
                      }
                      setSelectedSpendingIds([]);
                    }}
                    className="h-4 w-4 rounded border-[color:var(--field-border)]"
                    aria-label="Select all budget items"
                  />
                </th>
                <th className="sticky top-0 z-10 bg-[color:var(--surface-strong)] px-3 py-3 font-semibold sm:px-4">
                  {budgetText[language].table.item}
                </th>
                <th className="sticky top-0 z-10 bg-[color:var(--surface-strong)] px-3 py-3 font-semibold sm:px-4">
                  <button
                    type="button"
                    data-tab-trigger="true"
                    onClick={() =>
                      setPlanSortDirection((prev) => (prev === "desc" ? "asc" : "desc"))
                    }
                    className="inline-flex items-center gap-2 text-left uppercase tracking-widest text-[color:var(--muted)] transition hover:text-[color:var(--foreground)]"
                    aria-label={
                      language === "kh"
                        ? `តម្រៀប ${budgetText[language].table.plan}`
                        : `Sort ${budgetText[language].table.plan}`
                    }
                    title={
                      planSortDirection === "desc"
                        ? language === "kh"
                          ? "តម្រៀបពីច្រើនទៅតិច"
                          : "Sort high to low"
                        : language === "kh"
                          ? "តម្រៀបពីតិចទៅច្រើន"
                          : "Sort low to high"
                    }
                  >
                    <span>{budgetText[language].table.plan}</span>
                    <span className="text-[11px]">
                      {planSortDirection === "desc" ? "↓" : "↑"}
                    </span>
                  </button>
                </th>
                <th className="sticky top-0 z-10 bg-[color:var(--surface-strong)] px-3 py-3 font-semibold sm:px-4">
                  {budgetText[language].table.current}
                </th>
                <th className="sticky top-0 z-10 bg-[color:var(--surface-strong)] px-3 py-3 font-semibold sm:px-4">
                  {budgetText[language].table.left}
                </th>
                <th className="sticky top-0 z-10 bg-[color:var(--surface-strong)] px-3 py-3 font-semibold sm:px-4">
                  {budgetText[language].table.notes}
                </th>
                <th className="sticky top-0 z-10 bg-[color:var(--surface-strong)] px-3 py-3 text-right font-semibold sm:px-4">
                  {budgetText[language].table.action}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--divider)] bg-[color:var(--surface)]">
              {spending.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-sm text-[color:var(--muted)]">
                    {budgetText[language].noItems}
                  </td>
                </tr>
              ) : (
                sortedSpending.map((item) => {
                  const rowBalance = {
                    USD:
                      (item.budgetCurrency === "USD" ? item.budget : 0) -
                      (item.actualCurrency === "USD" ? item.actual : 0),
                    Riel:
                      (item.budgetCurrency === "Riel" ? item.budget : 0) -
                      (item.actualCurrency === "Riel" ? item.actual : 0),
                  };
                  return (
                    <tr key={item.id}>
                      <td className="px-2 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedSpendingIds.includes(item.id)}
                          onChange={(event) => {
                            setSelectedSpendingIds((prev) =>
                              event.target.checked
                                ? prev.includes(item.id)
                                  ? prev
                                  : [...prev, item.id]
                                : prev.filter((id) => id !== item.id),
                            );
                          }}
                          className="h-4 w-4 rounded border-[color:var(--field-border)]"
                          aria-label={`Select ${item.item}`}
                        />
                      </td>
                      <td className="px-3 py-3 align-top sm:px-4">
                        <p className="max-w-[12rem] break-words font-medium text-[color:var(--foreground)] sm:max-w-[16rem]">
                          {item.item}
                        </p>
                      </td>
                      <td className="px-3 py-3 align-top sm:px-4">
                        <span className="whitespace-nowrap">{formatAmountByCurrency(item.budget, item.budgetCurrency)}</span>
                      </td>
                      <td className="px-3 py-3 align-top sm:px-4">
                        <span className="whitespace-nowrap">{formatAmountByCurrency(item.actual, item.actualCurrency)}</span>
                      </td>
                      <td className="px-3 py-3 align-top text-[color:var(--muted)] sm:px-4">
                        {item.budgetCurrency === item.actualCurrency ? (
                          <span className="whitespace-nowrap">
                            {formatAmountByCurrency(item.budget - item.actual, item.budgetCurrency)}
                          </span>
                        ) : (
                          <div className="space-y-1">
                            {rowBalance.USD !== 0 ? <div>{formatAmountByCurrency(rowBalance.USD, "USD")}</div> : null}
                            {rowBalance.Riel !== 0 ? <div>{formatAmountByCurrency(rowBalance.Riel, "Riel")}</div> : null}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 align-top text-[color:var(--muted)] sm:px-4">
                        <p className="max-w-[14rem] break-words sm:max-w-[18rem]">{item.note || "-"}</p>
                      </td>
                      <td className="px-3 py-3 text-right align-top sm:px-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            data-tab-trigger="true"
                            onClick={() => startEditSpending(item)}
                            className={`flex h-8 w-8 items-center justify-center rounded-full transition ${
                              editingSpendingId === item.id
                                ? "bg-amber-500 text-white shadow-sm dark:bg-amber-500"
                                : "border border-amber-300 bg-amber-50 text-amber-700 shadow-sm hover:bg-amber-100 dark:border-amber-400/30 dark:bg-amber-400/15 dark:text-amber-200 dark:hover:bg-amber-400/25"
                            }`}
                            aria-label={budgetText[language].edit}
                            title={budgetText[language].edit}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              className="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="M3 21h6" />
                              <path d="M14.5 4.5a2.12 2.12 0 0 1 3 3L8 17l-4 1 1-4 9.5-9.5Z" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            data-tab-trigger="true"
                            onClick={() => requestDeleteSpendingRow(item.id)}
                            className="flex h-8 w-8 items-center justify-center rounded-full bg-red-600 text-white shadow-sm transition hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600"
                            aria-label={budgetText[language].delete}
                            title={budgetText[language].delete}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              className="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="M3 6h18" />
                              <path d="M8 6V4.75A1.75 1.75 0 0 1 9.75 3h4.5A1.75 1.75 0 0 1 16 4.75V6" />
                              <path d="M19 6l-1 13.25A1.75 1.75 0 0 1 16.26 21H7.74A1.75 1.75 0 0 1 6 19.25L5 6" />
                              <path d="M10 10.5v6" />
                              <path d="M14 10.5v6" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
          </tbody>
          <tfoot className="bg-[color:var(--surface)] text-sm font-semibold">
            <tr className="bg-[color:var(--surface-strong)]">
              <td />
              <td className="px-3 py-3 text-[color:var(--foreground)] sm:px-4">
                {budgetText[language].table.totalsLabel}
              </td>
              <td className="px-3 py-3 text-emerald-600 dark:text-emerald-400 sm:px-4">
                <div className="space-y-1">
                  <div>{formatAmountByCurrency(budgetSummaryTotals.plan.USD, "USD")}</div>
                  <div>{formatAmountByCurrency(budgetSummaryTotals.plan.Riel, "Riel")}</div>
                </div>
              </td>
              <td className="px-3 py-3 text-amber-700 dark:text-amber-400 sm:px-4">
                <div className="space-y-1">
                  <div>{formatAmountByCurrency(budgetSummaryTotals.actual.USD, "USD")}</div>
                  <div>{formatAmountByCurrency(budgetSummaryTotals.actual.Riel, "Riel")}</div>
                </div>
              </td>
              <td className="px-3 py-3 text-rose-700 dark:text-rose-500 sm:px-4">
                <div className="space-y-1">
                  <div>{formatAmountByCurrency(budgetSummaryTotals.left.USD, "USD")}</div>
                  <div>{formatAmountByCurrency(budgetSummaryTotals.left.Riel, "Riel")}</div>
                </div>
              </td>
              <td className="px-3 py-3 text-[color:var(--muted)] sm:px-4">-</td>
              <td />
            </tr>
          </tfoot>
        </table>
          </div>
        </div>
        <div className="mt-4 overflow-hidden rounded-2xl border border-[color:var(--field-border)] bg-white shadow-[0_12px_30px_rgba(61,89,122,0.10)] dark:bg-[color:var(--surface)] dark:shadow-[0_18px_42px_rgba(0,0,0,0.3)]">
          <div className="grid divide-y divide-[color:var(--divider)] sm:grid-cols-2 sm:divide-x sm:divide-y-0">
            <div className="bg-white dark:bg-[color:var(--surface)]">
              <div className="border-b border-[color:var(--divider)] bg-[color:var(--surface-strong)] px-5 py-3">
                <span className="inline-flex rounded-lg border border-emerald-700 bg-emerald-700 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white shadow-sm dark:border-emerald-300 dark:bg-emerald-300 dark:text-emerald-950">
                  {budgetText[language].planAmount}
                </span>
              </div>
              <div className="divide-y divide-[color:var(--divider)]">
                <div className="flex items-center justify-between gap-4 px-5 py-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                    {budgetText[language].totalUsd}
                  </span>
                  <span className="break-words text-right text-base font-extrabold text-emerald-800 dark:text-emerald-300 sm:text-lg">
                    {formatAmountByCurrency(budgetSummaryTotals.plan.USD, "USD")}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-4 px-5 py-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                    {budgetText[language].totalRiel}
                  </span>
                  <span className="break-words text-right text-sm font-extrabold text-emerald-800 dark:text-emerald-300">
                    {formatAmountByCurrency(budgetSummaryTotals.plan.Riel, "Riel")}
                  </span>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-[color:var(--surface)]">
              <div className="border-b border-[color:var(--divider)] bg-[color:var(--surface-strong)] px-5 py-3">
                <span className="inline-flex rounded-lg border border-amber-700 bg-amber-700 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white shadow-sm dark:border-amber-300 dark:bg-amber-300 dark:text-amber-950">
                  {budgetText[language].planBalanceLabel}
                </span>
              </div>
              <div className="divide-y divide-[color:var(--divider)]">
                <div className="flex items-center justify-between gap-4 px-5 py-3">
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                    {budgetText[language].planAmount}
                  </span>
                  <span className="text-sm font-extrabold text-slate-950 dark:text-white">
                    {formatCurrency(displayedTotals.budgetTotal)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-4 px-5 py-3">
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                    {budgetText[language].currentAmount}
                  </span>
                  <span className="text-sm font-extrabold text-slate-950 dark:text-white">
                    {formatCurrency(displayedTotals.actualTotal)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-4 bg-amber-50/70 px-5 py-4 dark:bg-amber-400/10">
                  <span className="text-xs font-extrabold uppercase tracking-[0.14em] text-amber-800 dark:text-amber-200">
                    Final Result Money
                  </span>
                  <span className="break-words text-right text-base font-extrabold text-amber-900 dark:text-amber-300 sm:text-lg">
                    {formatCurrency(
                      budgetSummaryTotals.plan.USD - (displayedTotals.budgetTotal - displayedTotals.actualTotal),
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
